import { useEffect, useState, useRef, useCallback } from "react";
import useDebounce from "@/hooks/useDebounce";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { useToast } from "@/hooks/useToast";
import type { Municipality } from "@/context/MunicipalityContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";

declare const Honeybadger: {
  notify: (error: unknown) => void;
};

interface NewChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMunicipality: string;
  onMunicipalityChange: (value: string) => void;
  onStartChat: (municipalityId: number, addressData?: Record<string, unknown>) => void;
  municipalities: Municipality[];
  title?: string;
  description?: string;
  municipalityPlaceholder?: string;
  startButtonLabel?: string;
  includeAddressFields?: boolean;
  /** When set, skip the municipality picker and fix the active municipality (address fields still blank). */
  lockedMunicipalityId?: number;
}

interface AddressSuggestion {
  mapbox_id: string;
  display_name: string;
  address: string;
  unit?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

const INITIAL_FORM_VALUES = {
  address: "",
  unit: "",
  city: "",
  parcelId: "",
};

const FIELD_PLACEHOLDERS = {
  address: "123 Main St",
  unit: "101",
  city: "Pleasantville",
  parcelId: "03-00817-000",
} as const;

type AddressField = keyof typeof INITIAL_FORM_VALUES;

type AddressStatus = {
  status?: "valid" | "invalid";
  message?: string;
  suggestedAddress?: Partial<Record<AddressField, string>>;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type ValidationSource = "suggestion_selected" | "manual_submit" | "live_lookup";

/** API should send string[]; coerce legacy mistakes like a bare string (e.g. Ruby %[x] vs %w[x]). */
function normalizeAddressFieldList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string" && value.length > 0) {
    return [value];
  }
  return [];
}

function getAddressLookupErrorMessage(
  status: number,
  error?: string,
  errorCode?: string,
): string {
  if (errorCode === "address_not_found") {
    return "This address isn't in the municipality's GIS records. Check the house number and street, or start a chat without it.";
  }
  if (errorCode === "municipality_not_found" || error?.includes("Municipality not found")) {
    return error || "Municipality not found.";
  }
  // Base mirrors GeospatialService::ServiceError::USER_FACING_UPSTREAM_ERROR
  // (app/services/geospatial_service/service_error.rb) — source of truth.
  // API stays context-neutral; this surface appends its own continuation.
  const upstreamGisUnavailableBase =
    "The municipality's GIS is temporarily unavailable.";
  const chatWithoutAddressHint = "You can still start a chat without an address.";
  const upstreamGisUnavailableMessage = `${upstreamGisUnavailableBase} ${chatWithoutAddressHint}`;
  if (errorCode === "upstream_gis_unavailable") {
    const base = error || upstreamGisUnavailableBase;
    return `${base} ${chatWithoutAddressHint}`;
  }
  if (errorCode === "service_unavailable" || status === 503) {
    // Mid-deploy legacy backends may still put raw upstream text in `error`
    // (e.g. "Decatur GIS network error: Connection refused"). Never surface that.
    return upstreamGisUnavailableMessage;
  }
  return error || "We're having trouble validating that address. You can still start a chat without it.";
}

interface ValidateAddressOptions {
  includeSuggestedAddress?: boolean;
}

function NewChatDialog({
  open,
  onOpenChange,
  initialMunicipality,
  onMunicipalityChange,
  onStartChat,
  municipalities,
  title = "Select a Municipality",
  description = "Choose a municipality to start a new chat.",
  municipalityPlaceholder = "Select a municipality",
  startButtonLabel = "Start New Chat",
  includeAddressFields = true,
  lockedMunicipalityId,
}: NewChatDialogProps) {
  const singleMunicipality = municipalities.length === 1 ? municipalities[0] : null;
  const isMunicipalityLocked = typeof lockedMunicipalityId === "number" && !Number.isNaN(lockedMunicipalityId);
  const activeMunicipalityId = isMunicipalityLocked
    ? String(lockedMunicipalityId)
    : (singleMunicipality ? singleMunicipality.id.toString() : initialMunicipality);
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [addressStatus, setAddressStatus] = useState<AddressStatus>({});
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);
  const [isAddressInputFocused, setIsAddressInputFocused] = useState(false);
  const validatedAddressRef = useRef<Record<string, unknown> | null>(null);
  const selectingSuggestionRef = useRef(false);
  const hasAppliedSuggestionRef = useRef(false);
  const debouncedFormValues = useDebounce(formValues, 500);
  const debouncedAddressQuery = useDebounce(formValues.address, 350);

  const selectedMunicipality = municipalities?.find((m) => m.id.toString() === activeMunicipalityId);
  const addressLookup = includeAddressFields ? selectedMunicipality?.address_lookup : undefined;
  const supportedFields = includeAddressFields
    ? normalizeAddressFieldList(addressLookup?.supported_fields ?? addressLookup?.required_fields)
    : [];
  const fuzzySearchEnabled = Boolean(addressLookup?.fuzzy_search_enabled && supportedFields.includes("address"));
  const legacyValidationEnabled = Boolean(addressLookup && !fuzzySearchEnabled);

  useEffect(() => {
    if (!open || !singleMunicipality || initialMunicipality === activeMunicipalityId) {
      return;
    }

    onMunicipalityChange(activeMunicipalityId);
  }, [activeMunicipalityId, initialMunicipality, onMunicipalityChange, open, singleMunicipality]);

  const trackAddressEvent = useCallback((eventName: string, properties: Record<string, unknown> = {}) => {
    const posthog = (window as Window & { posthog?: PostHogLike }).posthog;
    posthog?.capture(eventName, {
      municipality_id: selectedMunicipality?.id,
      municipality_name: selectedMunicipality?.name,
      provider: addressLookup?.provider,
      ...properties,
    });
  }, [addressLookup?.provider, selectedMunicipality?.id, selectedMunicipality?.name]);

  const resetLookupState = useCallback(() => {
    setAddressStatus({});
    setAddressSuggestions([]);
    setSelectedSuggestionId(null);
    validatedAddressRef.current = null;
  }, []);

  const updateValue = (field: AddressField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    resetLookupState();
    setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validateAddress = useCallback(async (
    values: typeof INITIAL_FORM_VALUES,
    source: ValidationSource,
    options: ValidateAddressOptions = {},
  ) => {
    if (!activeMunicipalityId) return null;

    const payload = {
      municipality_id: Number(activeMunicipalityId),
      address: values.address,
      unit: values.unit,
      city: values.city,
      parcelId: values.parcelId,
    };

    setValidationLoading(true);

    try {
      const response = await fetch("/api/v1/address_lookups", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = getAddressLookupErrorMessage(response.status, data.error, data.error_code);
        setAddressStatus({
          status: "invalid",
          message,
          suggestedAddress: undefined,
        });
        validatedAddressRef.current = null;
        trackAddressEvent("address_search_lookup_failed", {
          source,
          status: response.status,
          error_code: data.error_code,
        });
        return null;
      }

      validatedAddressRef.current = data;
      setAddressStatus({
        status: "valid",
        message: `${options.includeSuggestedAddress ? "Valid" : "Validated"} address: ${data.address}`,
        suggestedAddress: options.includeSuggestedAddress ? {
          address: data.address,
          unit: data.unit,
          city: data.city,
          parcelId: data.parcelId,
        } : undefined,
      });
      trackAddressEvent("address_search_lookup_succeeded", {source});
      return data;
    } catch (error) {
      Honeybadger.notify(error);
      setAddressStatus({
        status: "invalid",
        message: "We're having trouble validating that address. You can still start a chat without it.",
        suggestedAddress: undefined,
      });
      validatedAddressRef.current = null;
      trackAddressEvent("address_search_lookup_failed", {source});
      return null;
    } finally {
      setValidationLoading(false);
    }
  }, [activeMunicipalityId, trackAddressEvent]);

  const fetchAddressSuggestions = useCallback(async (query: string) => {
    if (!activeMunicipalityId) return;

    setSearchLoading(true);

    try {
      const params = new URLSearchParams({
        municipality_id: activeMunicipalityId,
        query,
      });
      const response = await fetch(`/api/v1/address_searches?${params.toString()}`, {
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch address suggestions");
      }

      const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
      setAddressSuggestions(suggestions);
      if (suggestions.length > 0) {
        trackAddressEvent("address_search_shown", {
          suggestion_count: suggestions.length,
        });
      }
    } catch (error) {
      Honeybadger.notify(error);
      setAddressSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  }, [activeMunicipalityId, trackAddressEvent]);

  const handleSelectSuggestion = async (suggestion: AddressSuggestion) => {
    if (selectingSuggestionRef.current) {
      return;
    }

    selectingSuggestionRef.current = true;

    const nextValues = {
      ...formValues,
      address: suggestion.address,
      unit: suggestion.unit || formValues.unit,
      city: suggestion.city || formValues.city,
    };

    setFormValues(nextValues);
    setSelectedSuggestionId(suggestion.mapbox_id);
    setAddressSuggestions([]);
    setIsAddressInputFocused(false);
    trackAddressEvent("address_search_selected", {mapbox_id: suggestion.mapbox_id});
    try {
      await validateAddress(nextValues, "suggestion_selected");
    } finally {
      selectingSuggestionRef.current = false;
    }
  };

  const fetchAddressSuggestionsForInput = useCallback(
    async () => {
      if (!open || !fuzzySearchEnabled) {
        setAddressSuggestions([]);
        return;
      }

      const query = debouncedAddressQuery.trim();
      if (selectedSuggestionId || query.length < 3) {
        setAddressSuggestions([]);
        return;
      }

      await fetchAddressSuggestions(query);
    },
    [debouncedAddressQuery, fetchAddressSuggestions, fuzzySearchEnabled, open, selectedSuggestionId],
  );

  useEffect(() => {
    fetchAddressSuggestionsForInput();
  }, [fetchAddressSuggestionsForInput]);

  useEffect(() => {
    if (!open || !legacyValidationEnabled) {
      return;
    }

    if (hasAppliedSuggestionRef.current) {
      hasAppliedSuggestionRef.current = false;
      return;
    }

    const hasEnoughInput =
      debouncedFormValues.address.trim().length > 4 || debouncedFormValues.parcelId.trim().length > 5;

    if (!hasEnoughInput) {
      return;
    }

    void validateAddress(debouncedFormValues, "live_lookup", {
      includeSuggestedAddress: true,
    });
  }, [debouncedFormValues, legacyValidationEnabled, open, validateAddress]);

  const { toast } = useToast();

  const handleStartChat = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const hasAddressInput = supportedFields.some((field) => formValues[field as AddressField].trim().length > 0);
      let addressData = validatedAddressRef.current;

      if (addressLookup && hasAddressInput && !addressData) {
        addressData = await validateAddress(formValues, "manual_submit");
      }

      if (addressLookup && hasAddressInput) {
        trackAddressEvent("address_search_chat_started", {
          had_address_data: Boolean(addressData),
        });
      }
      onOpenChange(false);
      onMunicipalityChange("");
      onStartChat(Number(activeMunicipalityId), addressData || undefined);
    } catch (error) {
      Honeybadger.notify(error);
      toast({ title: "Failed to create chat", description: "Please try again later", variant: "destructive" });
    }
  };

  useEffect(() => {
    if (!open) {
      setFormValues(INITIAL_FORM_VALUES);
      resetLookupState();
      setIsAddressInputFocused(false);
    }
  }, [open, resetLookupState]);

  useEffect(() => {
    if (!open) return;

    setFormValues(INITIAL_FORM_VALUES);
    resetLookupState();
  }, [initialMunicipality, open, resetLookupState]);

  const applySuggestedAddress = () => {
    if (!addressStatus.suggestedAddress) {
      return;
    }

    hasAppliedSuggestionRef.current = true;
    setFormValues((prev) => ({
      ...prev,
      ...addressStatus.suggestedAddress,
    }));
  };

  const showLegacySuggestedAddressButton = Boolean(
    legacyValidationEnabled &&
      addressStatus.status === "valid" &&
      addressStatus.suggestedAddress &&
      Object.entries(addressStatus.suggestedAddress).some(([field, value]) => {
        if (!value) return false;
        return formValues[field as AddressField] !== value;
      }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={handleStartChat}>
          {!singleMunicipality && !isMunicipalityLocked && (
            <Select value={initialMunicipality} onValueChange={onMunicipalityChange} required>
              <SelectTrigger>
                <SelectValue placeholder={municipalityPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {municipalities?.map((municipality) => (
                  <SelectItem key={municipality.id} value={municipality.id.toString()}>
                    {formatMunicipalityLabel(municipality)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {activeMunicipalityId &&
            supportedFields.map((field) => {
              const addressField = field as AddressField;
              const label = field === "parcelId" ? "Parcel ID" : field.charAt(0).toUpperCase() + field.slice(1);

              return (
                <div key={field} className="grid gap-2">
                  <label htmlFor={field} className="text-sm font-medium">
                    {label} (Optional)
                  </label>
                  {field === "address" ? (
                    <div className="relative">
                      <Input
                        id={field}
                        placeholder={FIELD_PLACEHOLDERS[addressField]}
                        value={formValues[addressField]}
                        onChange={updateValue(addressField)}
                        onFocus={() => setIsAddressInputFocused(true)}
                        onBlur={() => {
                          window.setTimeout(() => setIsAddressInputFocused(false), 100);
                        }}
                        autoComplete="off"
                      />
                      {fuzzySearchEnabled && isAddressInputFocused && formValues.address.trim().length >= 3 && (
                        <div
                          className="absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md"
                          onMouseDown={(event) => event.preventDefault()}
                        >
                          <Command shouldFilter={false}>
                            <CommandList>
                              {searchLoading ? <CommandEmpty>Searching addresses...</CommandEmpty> : null}
                              {!searchLoading && addressSuggestions.length === 0 ? (
                                <CommandEmpty>No matching addresses yet.</CommandEmpty>
                              ) : null}
                              <CommandGroup>
                                {addressSuggestions.map((suggestion) => (
                                  <CommandItem
                                    key={suggestion.mapbox_id}
                                    value={suggestion.display_name}
                                    onClick={() => void handleSelectSuggestion(suggestion)}
                                    onSelect={() => void handleSelectSuggestion(suggestion)}
                                  >
                                    {suggestion.display_name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Input
                      id={field}
                      placeholder={FIELD_PLACEHOLDERS[addressField]}
                      value={formValues[addressField]}
                      onChange={updateValue(addressField)}
                    />
                  )}
                </div>
              );
            })}

          {validationLoading && <p className="text-sm text-muted-foreground">Validating address...</p>}
          {addressStatus.message && (
            <div className="space-y-2">
              <p className={`text-sm ${addressStatus.status === "valid" ? "text-green-600" : "text-red-600"}`}>
                {addressStatus.message}
              </p>
              {showLegacySuggestedAddressButton && (
                <Button type="button" variant="secondary" size="sm" onClick={applySuggestedAddress} className="w-full">
                  Use Suggested Address
                </Button>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button disabled={!activeMunicipalityId || validationLoading}>{startButtonLabel}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default NewChatDialog;
